import { Link } from 'react-router-dom';
import { LegalPageLayout } from './LegalPageLayout';

const EFFECTIVE_DATE = 'March 1, 2026';
const VERSION = '1.0';

export default function ParentalConsent() {
  return (
    <LegalPageLayout>
      <h1>Parental Consent Addendum</h1>
      <p><strong>No Doubles Golf Co.</strong></p>
      <p><strong>Version:</strong> {VERSION}</p>
      <p><strong>Effective Date:</strong> {EFFECTIVE_DATE}</p>
      <p>
        This Parental Consent Addendum supplements and is incorporated into the No Doubles Golf
        Co. <Link to="/terms">Terms of Service</Link> ("Terms"). Capitalized terms not defined
        here have the meanings assigned in the Terms.
      </p>

      <hr />

      <h2>1. Applicability</h2>
      <p>
        This Addendum applies when a user between 13 and 17 years of age ("Minor User") uses the
        Service. The Service is not available to users under 13 under any circumstances.
      </p>

      <hr />

      <h2>2. Parental Consent Required</h2>
      <p>
        A Minor User may use the Service only with the verified consent of a parent or legal
        guardian ("Parent"). By providing consent, the Parent:
      </p>
      <ul>
        <li>Confirms they are the Minor User's parent or legal guardian</li>
        <li>
          Confirms they have reviewed and accepted the{' '}
          <Link to="/terms">Terms</Link>, the{' '}
          <Link to="/privacy">Privacy Policy</Link>, and all referenced addenda
        </li>
        <li>Accepts full legal and financial responsibility for the Minor User's use of the Service</li>
        <li>Consents to the collection and processing of the Minor User's data as described in the Privacy Policy</li>
      </ul>

      <hr />

      <h2>3. Consent Process</h2>
      <h3>3.1 Age Collection</h3>
      <p>
        During account creation, users are asked to select their age bracket. The Service uses a
        neutral age gate that does not coach the user toward selecting a particular age. This is
        consistent with Apple and Google guidance on age verification for non-COPPA applications
        (the Service does not permit users under 13).
      </p>
      <h3>3.2 Verification</h3>
      <p>
        When a user indicates they are under 18 during account creation, the Service will require
        verifiable parental consent before the account is activated. The consent process requires
        the Parent to:
      </p>
      <ul>
        <li>Provide their own name, email address, and relationship to the Minor User</li>
        <li>Confirm they have read and accepted the Terms, Privacy Policy, and this Addendum</li>
        <li>Acknowledge the physical activity risks described in Section 6 of the Terms</li>
        <li>
          Acknowledge the AI limitations described in Section 4 of the Terms and the{' '}
          <Link to="/terms/ai-disclosure">AI Disclosure Addendum</Link>
        </li>
        <li>
          Complete a verification step using one of the following FTC-recognized methods: (a)
          providing a credit or debit card for a nominal verification charge (which will be
          refunded), (b) providing a signed consent form returned via email, or (c) such other
          method as may be approved by the FTC for verifiable parental consent under COPPA and
          applicable law
        </li>
      </ul>
      <p>
        <em>
          <strong>Note:</strong> While COPPA's verifiable parental consent requirements apply to
          children under 13 (and the Service excludes users under 13), No Doubles implements a
          heightened consent standard for users 13–17 as a matter of best practice and to
          strengthen the enforceability of parental consent for contractual capacity purposes.
        </em>
      </p>
      <h3>3.3 Consent Record</h3>
      <p>
        No Doubles maintains a record of parental consent, including the date, method, and identity
        of the consenting Parent. This record will be retained for the duration of the Minor User's
        account and for a reasonable period thereafter as required by applicable law.
      </p>

      <hr />

      <h2>4. Parent Responsibilities</h2>
      <h3>4.1 Supervision</h3>
      <p>The Parent is responsible for supervising the Minor User, ensuring that they:</p>
      <ul>
        <li>Do not perform any exercise beyond their physical capability</li>
        <li>Stop any activity that causes pain or discomfort</li>
        <li>Have appropriate physical space and equipment</li>
        <li>Have received medical clearance if any medical condition exists</li>
      </ul>
      <h3>4.2 Fitness Assessment</h3>
      <p>
        The Parent is responsible for determining whether the Minor User is physically fit to
        perform exercises recommended by the Service. The Service does not assess the physical
        fitness, medical history, or developmental readiness of any user.
      </p>
      <h3>4.3 Financial Responsibility</h3>
      <p>
        The Parent is responsible for all charges incurred through the Minor User's account,
        including subscription fees, in-app purchases, and any marketplace fees.
      </p>
      <h3>4.4 Content Review</h3>
      <p>
        The Parent should periodically review the drill recommendations, exercise plans, and AI
        Outputs provided to the Minor User.
      </p>

      <hr />

      <h2>5. Assumption of Risk for Minor Users</h2>
      <h3>5.1 Inherent Risks</h3>
      <p>
        Golf practice and mobility exercises involve inherent risks of physical injury that may be
        heightened for adolescent users, including growth plate injuries, overuse injuries, muscle
        strains, and ligament sprains.
      </p>
      <h3>5.2 Voluntary Assumption</h3>
      <p>
        By consenting, the Parent voluntarily assumes all risks associated with the Minor User's
        physical activity in connection with the Service.
      </p>
      <h3>5.3 Waiver</h3>
      <p>
        To the maximum extent permitted by applicable law, the Parent releases No Doubles from all
        claims arising from the Minor User's use of the Service.
      </p>
      <p>
        <em>
          <strong>Note:</strong> Some jurisdictions restrict parents from waiving a minor's legal
          rights. In particular, California law (Cal. Civ. Code § 1668) restricts pre-injury
          waivers in many contexts, and courts in California and other states have declined to
          enforce parental waivers of a minor's negligence claims. In jurisdictions where this
          waiver is unenforceable as to the Minor User's own claims, it remains enforceable as to
          the Parent's claims to the maximum extent permitted by law.
        </em>
      </p>

      <hr />

      <h2>6. Data Handling for Minor Users</h2>
      <h3>6.1 Data Minimization</h3>
      <p>We collect only the data necessary to provide the Service. We do not:</p>
      <ul>
        <li>Sell Minor User data</li>
        <li>Use Minor User data for behavioral advertising</li>
        <li>Share Minor User data with third parties for marketing</li>
      </ul>
      <h3>6.2 Parental Access and Control</h3>
      <p>The Parent may:</p>
      <ul>
        <li>Request access to the Minor User's account data</li>
        <li>Request correction of personal information</li>
        <li>Request deletion of the account and associated data</li>
        <li>Revoke consent at any time</li>
      </ul>
      <h3>6.3 Consent Revocation</h3>
      <p>
        Upon revocation, the Minor User's account will be deactivated and data deleted in
        accordance with the <Link to="/privacy">Privacy Policy</Link>. Any active subscription
        will be cancelled at the end of the current billing period.
      </p>

      <hr />

      <h2>7. Coach Marketplace and Minor Users (When Available)</h2>
      <p>If the coach marketplace feature is available:</p>
      <ul>
        <li>
          <strong>
            At launch, marketplace access for Minor Users is restricted to asynchronous video
            analysis review only.
          </strong>{' '}
          Minor Users may not participate in live video, live chat, or real-time messaging with
          Coaches unless a Parent is present and actively participating in the session.
        </li>
        <li>The Parent must separately consent to the Minor User's participation in any marketplace interactions.</li>
        <li>The Parent is responsible for evaluating the suitability of any Coach.</li>
        <li>
          All Coach interactions with Minor Users are subject to the Coach's independent agreement
          with No Doubles, which includes codes of conduct, background check requirements, and
          obligations specific to interactions with minors.
        </li>
        <li>No Doubles reserves the right to further restrict or prohibit marketplace access for Minor Users at any time.</li>
      </ul>

      <hr />

      <h2>8. Age Misrepresentation</h2>
      <p>
        If No Doubles determines that a user has misrepresented their age to avoid the parental
        consent requirement, we may immediately suspend or terminate the account and delete all
        associated data.
      </p>

      <hr />

      <h2>Contact</h2>
      <p>No Doubles Golf Co.</p>
      <p>Email: support@nodoublesgolf.com</p>
    </LegalPageLayout>
  );
}
